import React, { Children } from 'react'
import { motion } from 'framer-motion'
import {
  SparklesIcon,
  MonitorIcon,
  CompassIcon,
  HandHeartIcon,
  UsersIcon,
  VideoIcon,
  ShieldCheckIcon,
  EyeIcon,
  CheckCircle2Icon,
} from 'lucide-react'
import { Container } from '../components/ui/Container'
import { Button } from '../components/ui/Button'
import { PathCard } from '../components/ui/PathCard'
import { GeometricAccent } from '../components/ui/GeometricAccent'
import { PatternDecor } from '../components/ui/PatternDecor'
import { MosqueArchMask } from '../components/ui/MosqueArchMask'
import { Link } from 'react-router-dom'
const ageGroups = [
  {
    title: 'Ages 5–7 | Foundations',
    body: 'A gentle introduction to learning, values, and belonging.',
    points: [
      'Basic Islamic values through stories',
      'Kindness, respect, and empathy',
      'Confidence in expressing identity',
    ],
    tone: 'sage',
  },
  {
    title: 'Ages 8–10 | Identity & Confidence',
    body: 'Building self-awareness and emotional strength.',
    points: [
      'Communication skills and friendships',
      'Dealing with differences',
      'Confidence in school and social settings',
    ],
    tone: 'sky',
  },
  {
    title: 'Ages 11–14 | Strength & Responsibility',
    body: 'Preparing children for real-life challenges.',
    points: [
      'Identity in a non-Muslim environment',
      'Peer pressure and self-respect',
      'Ethical decision-making',
    ],
    tone: 'rose',
  },
  {
    title: 'Ages 15–18 | Leadership & Life Skills',
    body: 'Supporting teenagers as they transition into adulthood with confidence.',
    points: [
      'Purpose and responsibility',
      'Foundations of self-respect',
      'Representation and leadership',
    ],
    tone: 'sand',
  },
] as const
const toneBg: Record<string, string> = {
  sage: 'bg-sage-100',
  sky: 'bg-[#EDF2F7]',
  rose: 'bg-[#F7EDE9]',
  sand: 'bg-sand-100',
}
export function Children() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden isolate">
        <PatternDecor side="right" y="middle" size="lg" />
        <GeometricAccent
          variant="dots"
          className="absolute top-8 left-6 w-32 h-32"
          opacity={0.4}
        />
        <Container className="relative pt-8 md:pt-12 pb-16 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <span className="text-forest-700 text-sm font-semibold">
                Children always come first
              </span>
              <h1 className="font-display text-5xl md:text-7xl text-ink-900 mt-3 leading-tight">
                Children
              </h1>
              <p className="mt-5 text-ink-700 leading-relaxed max-w-lg">
                Our children's programs support Muslim children growing up in
                non-Muslim countries — helping them develop confidence, strong
                values, and a sense of belonging.
              </p>
              <p className="mt-3 text-ink-700 leading-relaxed max-w-lg">
                We combine education, emotional support, and identity-building
                in a safe and respectful learning environment.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/en/children/kids-programs">
                  <Button variant="primary">Explore Kids Programs</Button>
                </Link>
                <Link to="/en/children/scholarships">
                  <Button variant="secondary">Scholarships</Button>
                </Link>
              </div>
            </div>
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.96,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              transition={{
                duration: 0.7,
              }}
              className="lg:col-span-6 relative"
            >
              <div className="relative w-full lg:w-[calc(100%+6rem)] lg:-ml-8 aspect-[200/148]">
                <MosqueArchMask
                  src="https://cdn.magicpatterns.com/uploads/nWWygtex9gxYzjLQoiCWG3/children-img-1.jpg"
                  alt="Children learning together"
                />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Explore */}
      <section className="py-16 md:py-24 bg-sage-50/60">
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
            Explore Children
          </h2>
          <p className="mt-4 text-ink-700">
            Choose a pathway that matches your child's needs — programs, online
            learning, values, and scholarships.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <PathCard
              icon={SparklesIcon}
              tone="sage"
              title="Kids Programs"
              description="Structured learning pathways for Muslim children in non-Muslim environments — confidence, values, belonging, and life skills."
              linkLabel="View programs"
              href="/en/children/kids-programs"
            />
            <PathCard
              icon={MonitorIcon}
              tone="sky"
              title="Online Learning"
              description="Live, interactive sessions in small groups — designed for safety, engagement, and consistent progress across countries and time zones."
              linkLabel="How it works"
              href="/en/children/online-learning"
            />
            <PathCard
              icon={CompassIcon}
              tone="rose"
              title="Values & Identity"
              description="Strong identity without isolation: akhlaq, calm confidence, respectful integration, and tools for navigating differences."
              linkLabel="Learn more"
              href="/en/children/values-identity"
            />
            <PathCard
              icon={HandHeartIcon}
              tone="sand"
              title="Scholarships"
              description="Partial and full scholarships for families who need support — allocated transparently through clear criteria and donor-backed seats."
              linkLabel="Support options"
              href="/en/children/scholarships"
            />
          </div>
        </Container>
      </section>

      {/* Balanced model */}
      <section className="py-16 md:py-24 relative overflow-hidden bg-white/60">
        <GeometricAccent
          variant="star"
          className="absolute top-10 right-10 w-32 h-32"
          opacity={0.18}
        />
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight max-w-2xl">
            A balanced program model
          </h2>
          <p className="mt-4 text-ink-700">
            Our children's programs combine identity building, Islamic
            foundations, and life skills — helping Muslim children grow with
            confidence in non-Muslim societies.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              {
                title: 'Core Learning',
                points:
                  'Identity • Confidence • Social skills • Ethics • Integration',
                tone: 'sage',
              },
              {
                title: 'Islamic & Cultural Foundation',
                points:
                  'Islamic values • Arabic basics • Quran: recitation basics & meanings • Culture & heritage',
                tone: 'rose',
              },
              {
                title: 'Enrichment & Hobbies',
                points: 'Languages • Calligraphy • Creative classes • Projects',
                tone: 'sand',
              },
            ].map((b, i) => (
              <motion.div
                key={b.title}
                initial={{
                  opacity: 0,
                  y: 16,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  margin: '-60px',
                }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.08,
                }}
                className={`rounded-2xl p-6 ${toneBg[b.tone]} border border-ink-900/5`}
              >
                <h3 className="font-display text-lg text-ink-900">{b.title}</h3>
                <p className="mt-3 text-ink-700 text-sm leading-relaxed">
                  {b.points}
                </p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Age groups */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-[#EDF2F7]/60">
        <PatternDecor side="left" y="bottom" size="lg" />
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
            Age groups & pathways
          </h2>
          <p className="mt-4 text-ink-700">
            Each age group follows a structured curriculum aligned to children's
            emotional and developmental needs.
          </p>
          <p className="mt-3 text-ink-700 leading-relaxed">
            <strong className="text-ink-900">Note on Group Formats:</strong>{' '}
            Our early childhood tracks (Ages 5–10) welcome mixed cohorts to
            encourage peer learning. For our pre-teen and teenage programs
            (Ages 11–14 and 15–18), classes are conducted in dedicated
            gender-specific environments to respect privacy and Islamic
            guidelines for older children.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {ageGroups.map((g, i) => (
              <motion.div
                key={g.title}
                initial={{
                  opacity: 0,
                  y: 16,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  margin: '-60px',
                }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.06,
                }}
                className={`rounded-2xl p-6 md:p-7 ${toneBg[g.tone]} border border-ink-900/5`}
              >
                <h3 className="font-display text-xl text-ink-900">{g.title}</h3>
                <p className="mt-2 text-ink-700">{g.body}</p>
                <ul className="mt-4 space-y-2">
                  {g.points.map((p) => (
                    <li
                      key={p}
                      className="flex items-center gap-2 text-[14px] text-ink-700"
                    >
                      <CheckCircle2Icon
                        size={14}
                        className="text-forest-700 flex-shrink-0"
                        strokeWidth={1.6}
                      />
                      {p}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Online safe */}
      <section className="py-16 md:py-24 bg-white/60">
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
            Online learning that feels safe
          </h2>
          <p className="mt-4 text-ink-700">
            Safety, respect, and inclusion are central to every session.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {[
              {
                icon: UsersIcon,
                title: 'Small groups',
                text: '8–10 children for real participation and connection.',
                tone: 'sage',
              },
              {
                icon: VideoIcon,
                title: 'Live, interactive sessions',
                text: 'Not passive screen time — guided discussion and activities.',
                tone: 'sky',
              },
              {
                icon: ShieldCheckIcon,
                title: 'Moderation & wellbeing',
                text: 'A calm structure that protects without isolating.',
                tone: 'rose',
              },
              {
                icon: EyeIcon,
                title: 'Parent visibility',
                text: 'Clear guidance and regular feedback for parents.',
                tone: 'sand',
              },
            ].map((f) => (
              <div
                key={f.title}
                className={`rounded-2xl p-6 ${toneBg[f.tone]} border border-ink-900/5 flex gap-4`}
              >
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white text-forest-700 flex-shrink-0 border border-forest-700/10">
                  <f.icon size={18} strokeWidth={1.6} />
                </span>
                <div>
                  <h3 className="font-display text-lg text-ink-900">
                    {f.title}
                  </h3>
                  <p className="mt-1 text-ink-700 text-sm">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-sand-50/60">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
                Values & identity
              </h2>
              <p className="mt-4 text-ink-700">
                Islamic identity should be a source of strength — not fear,
                isolation, or pressure.
              </p>
              <ul className="mt-6 rounded-2xl bg-sage-50 border border-sage-200 p-6 space-y-3">
                {[
                  'Understand who they are',
                  'Respect others while respecting themselves',
                  'Develop strong character (akhlaq)',
                  'Navigate cultural differences with confidence',
                ].map((v) => (
                  <li key={v} className="flex items-center gap-3 text-ink-700">
                    <CheckCircle2Icon
                      size={16}
                      className="text-forest-700 flex-shrink-0"
                      strokeWidth={1.6}
                    />
                    {v}
                  </li>
                ))}
              </ul>
            </div>
            <motion.div
              initial={{
                opacity: 0,
                x: 20,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              viewport={{
                once: true,
                margin: '-80px',
              }}
              transition={{
                duration: 0.6,
              }}
              className="lg:col-span-6 relative"
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-card">
                <img
                  src="https://cdn.magicpatterns.com/uploads/5AD965Z9n7QyUcMmg3hGiN/children-img-2.jpg"
                  alt="Children with learning cards"
                  className="w-full h-full object-cover"
                />
              </div>
              <GeometricAccent
                variant="star"
                className="absolute -bottom-6 -left-6 w-32 h-32"
                opacity={0.18}
              />
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Access for every child */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-[#F7EDE9]/60">
        <PatternDecor side="right" y="top" size="md" />
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
            Access for every child
          </h2>
          <p className="mt-4 text-ink-700">
            No child should be excluded from support due to financial
            circumstances. Scholarships are supported through donations and
            institutional partnerships and allocated transparently.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              {
                title: 'Partial scholarships',
                text: 'Support for families with limited income or temporary difficulties.',
                tone: 'sage',
              },
              {
                title: 'Full scholarships',
                text: 'Priority support for vulnerable children, including disability and crisis situations.',
                tone: 'rose',
              },
              {
                title: 'Community-sponsored seats',
                text: 'Donor- and partner-funded places allocated with clear criteria and reporting.',
                tone: 'sand',
              },
            ].map((s) => (
              <div
                key={s.title}
                className={`rounded-2xl p-6 ${toneBg[s.tone]} border border-ink-900/5`}
              >
                <h3 className="font-display text-lg text-ink-900">{s.title}</h3>
                <p className="mt-2 text-ink-700 text-sm leading-relaxed">
                  {s.text}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Ready */}
      <section className="py-16 md:py-24 bg-white/60">
        <Container>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="max-w-xl">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
                Ready to begin?
              </h2>
              <p className="mt-4 text-ink-700">
                Choose a program that matches your child's age and needs — and
                start building confidence, values, and belonging in a safe
                learning space.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/en/children/kids-programs">
                <Button variant="primary">View Kids Programs</Button>
              </Link>
              <Link to="/en/children/scholarships">
                <Button variant="secondary">Apply for a Scholarship</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}
